import {
  getGasMarketYearlyDifferenceChart,
  getGasMarketYearlyDifferenceComparisonTable,
  getGasMarketYearlyDifferenceDailyIndicators,
} from "$/api/market-monitoring/gas-analysis.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import LineGraph from "$/lib/components/graphs/LineGraph";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import { CheckboxSet } from "$/lib/components/molecules/ChecboxSet";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import GraphDateRangeSelector from "$/lib/components/molecules/GraphDateRangeSelector";
import TableGraphWidget from "$/lib/components/widgets/TableGraphWidget";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { MarketYearlyDifferenceTableType } from "$/lib/types/api/market-monitoring/gas-analysis.types";
import type { CellType, ColumnType } from "$/lib/types/Graphs.types";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import type { FutureProductPeriod } from "$/lib/types/models/FutureProduct.types";
import {
  format,
  getDate,
  getLast6MonthsStartAndEndDates,
} from "$/lib/utils/functions/date.functions";
import {
  handleOnChangeDefaultGraphsTabs,
  PEG_GAS_COLORS,
  TTF_GAS_COLORS,
} from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import {
  cn,
  getArrayKeys,
  getMinMax,
} from "$/lib/utils/functions/misc.functions";
import { numberFormatter } from "$/lib/utils/functions/util.functions";
import { useQuery } from "@tanstack/react-query";
import type { CellContext } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";

const PEG_COLORS = [...PEG_GAS_COLORS];

const TTF_COLORS = [...TTF_GAS_COLORS];

const COLORS = [...PEG_COLORS, ...TTF_COLORS];

const LocalRenderTableNumberCell = <T extends object>({
  theme,
  className,
  ...cell
}: CellContext<T, number | string> & {
  theme: "blue" | "orange" | "red";
  className?: string;
}) => {
  const isNotANumber = !cell.getValue() && typeof cell !== "number";

  return (
    <span
      className={cn(
        `text-xs font-medium`,
        {
          "text-[#6177a9]": theme === "blue",
          "text-warning": theme === "orange",
          "text-danger": theme === "red",
          "text-black": isNotANumber,
        },
        className,
      )}
    >
      {isNotANumber ? "-" : numberFormatter(cell.getValue())}
    </span>
  );
};

type Props = {
  selectedDate: Date;
} & CustomDashboardProps;

const GasCalendarsMarketDifference = ({
  selectedDate,
  id,
  isDraggable = false,
  isShowcase = false,
}: Props) => {
  const [selectedGraphTabId, setSelectedGraphTabId] = useState("LAST_6_MONTHS");

  const { start, end } = getLast6MonthsStartAndEndDates();

  const [filters, setFilters] = useState<{
    peg: boolean;
    ttf: boolean;
  }>({
    peg: true,
    ttf: false,
  });

  const [tablePeriod, setTablePeriod] = useState<FutureProductPeriod | "DAY">(
    "QUARTER",
  );

  const [dateRange, setDateRange] = useState<[Date, Date]>([start, end]);

  const { data, isLoading } = useQuery({
    queryKey: ["get-gas-market-yearly-difference-chart", dateRange],
    queryFn: () =>
      getGasMarketYearlyDifferenceChart({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
      }),
  });

  const { data: tableData } = useQuery({
    queryKey: [
      "get-gas-analysis-future-yearly-difference-comparison-table",
      dateRange,
      tablePeriod,
    ],
    queryFn: () =>
      getGasMarketYearlyDifferenceComparisonTable({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
        period: tablePeriod,
      }),
  });

  const { data: daily } = useQuery({
    queryKey: ["get-gas-market-yearly-difference-daily", selectedDate],
    queryFn: () =>
      getGasMarketYearlyDifferenceDailyIndicators({
        date: getDate(selectedDate),
      }),
  });

  const toDisplay = useMemo(
    () =>
      data
        ? filters.peg && filters.ttf
          ? data.mergedData
          : filters.peg
            ? data.pegData
            : filters.ttf
              ? data.ttfData
              : []
        : [],
    [data, filters],
  );

  const keys = useMemo(() => getArrayKeys(toDisplay), [toDisplay]);

  const handleOnChangeDateRange = (values: [Date, Date]) => {
    setDateRange(values);
  };

  const colors: Record<string, string> = useMemo(
    () =>
      keys.reduce(
        (acc, key, i) => ({
          ...acc,
          [key]:
            filters.peg && filters.ttf
              ? COLORS[i]
              : filters.peg
                ? PEG_COLORS[i]
                : filters.ttf
                  ? TTF_COLORS[i]
                  : COLORS[i],
        }),
        {},
      ),
    [keys, filters],
  );

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  const handleOnChangeGraphVisiblity = (key: string, value: boolean) => {
    setGraphVisibility((prev) =>
      prev.map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    );
  };

  const pegTableKeys = ["pegCal1_2", "pegCal1_3", "pegCal2_3"];
  const ttfTableKeys = ["ttfCal1_2", "ttfCal1_3", "ttfCal2_3"];

  const tableBounds = useMemo(() => {
    if (tableData) {
      const keysToCompare =
        filters.peg && filters.ttf
          ? [...pegTableKeys, ...ttfTableKeys]
          : filters.peg
            ? pegTableKeys
            : ttfTableKeys;

      return getMinMax(tableData[tablePeriod], keysToCompare);
    }

    return { min: { value: 0 }, max: { value: 0 } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, tablePeriod, filters]);

  const columns: ColumnType<MarketYearlyDifferenceTableType>[] = useMemo(() => {
    const firstHeaders: ColumnType<MarketYearlyDifferenceTableType>[] = [
      {
        header: () => {},
        accessorKey: "year",
        cell: (cell) => {
          const values = cell.row.original;

          const display =
            tablePeriod === "QUARTER" || tablePeriod === "MONTH"
              ? [values.quarter, values.month]
              : [values.month, values.day];

          return (
            <Flexbox className="gap-0.5 whitespace-nowrap">
              <span className="text-xs font-medium text-black">
                {cell.getValue()}
              </span>

              <span className="text-[10px] text-tertiary-600">
                {display
                  .filter((v) => v)
                  .join(tablePeriod === "DAY" ? " " : " - ")}
              </span>
            </Flexbox>
          );
        },
      },
    ];

    return [
      ...firstHeaders,
      ...(filters.peg
        ? [
            {
              header: "PEG - Ecart Cal +1/+2",
              accessorKey: "pegCal1_2",
              cell: (cell: CellType<MarketYearlyDifferenceTableType>) => (
                <LocalRenderTableNumberCell theme="blue" {...cell} />
              ),
            },
            {
              header: "PEG - Ecart Cal +1/+3",
              accessorKey: "pegCal1_3",
              cell: (cell: CellType<MarketYearlyDifferenceTableType>) => (
                <LocalRenderTableNumberCell theme="orange" {...cell} />
              ),
            },
            {
              header: "PEG - Ecart Cal +2/+3",
              accessorKey: "pegCal2_3",
              cell: (cell: CellType<MarketYearlyDifferenceTableType>) => (
                <LocalRenderTableNumberCell theme="red" {...cell} />
              ),
            },
          ]
        : []),
      ...(filters.ttf
        ? [
            {
              header: "TTF - Ecart Cal +1/+2",
              accessorKey: "ttfCal1_2",
              cell: (cell: CellType<MarketYearlyDifferenceTableType>) => (
                <LocalRenderTableNumberCell
                  theme="blue"
                  className={cn(filters.peg && filters.ttf && `text-[#64a34b]`)}
                  {...cell}
                />
              ),
            },
            {
              header: "TTF - Ecart Cal +1/+3",
              accessorKey: "ttfCal1_3",
              cell: (cell: CellType<MarketYearlyDifferenceTableType>) => (
                <LocalRenderTableNumberCell
                  theme="orange"
                  className={cn(filters.peg && filters.ttf && `text-[#273c7b]`)}
                  {...cell}
                />
              ),
            },
            {
              header: "TTF - Ecart Cal +2/+3",
              accessorKey: "ttfCal2_3",
              cell: (cell: CellType<MarketYearlyDifferenceTableType>) => (
                <LocalRenderTableNumberCell
                  theme="red"
                  className={cn(filters.peg && filters.ttf && `text-[#784b7a]`)}
                  {...cell}
                />
              ),
            },
          ]
        : []),
    ];
  }, [filters, tablePeriod]);

  useEffect(() => {
    handleOnChangeDefaultGraphsTabs(
      selectedGraphTabId,
      handleOnChangeDateRange,
    );
  }, [selectedGraphTabId]);

  useEffect(() => {
    setGraphVisibility(
      keys.map((key, i) => ({
        name: key,
        isVisible: true,
        color:
          filters.peg && filters.ttf
            ? COLORS[i]
            : filters.peg
              ? PEG_COLORS[i]
              : filters.ttf
                ? TTF_COLORS[i]
                : COLORS[i],
      })),
    );
  }, [keys, filters]);

  return (
    <DisplayBox
      title="Ecart entre les Cal+1, Cal+2 et Cal+3"
      Icon={LineChartIcon}
      theme="green"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      className="rounded-xl shadow-displayBoxShadow"
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer midHeight>
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <Flexbox fullWidth fullHeight className="gap-2 rounded-3xl">
            <LineGraph
              data={toDisplay}
              graphVisibility={graphVisibility}
              title="Prix Stlt EEX"
              xAxisDataKey="date"
              colors={colors}
              theme="green"
              keys={keys}
              height={530}
              leftMargin={-10}
            />
          </Flexbox>
        )}

        <FiltersDisplayBox>
          <GraphDateRangeSelector
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedGraphTabId={selectedGraphTabId}
            setSelectedGraphTabId={setSelectedGraphTabId}
            theme="green"
          />

          <CheckboxSet<"peg" | "ttf">
            id="gas-market-calendar-difference-chart"
            title="Marché"
            selected={filters}
            onChange={(id, value) =>
              setFilters((prev) => {
                const newValue = { ...prev, [id]: value };
                if (!newValue.peg && !newValue.ttf) return prev;
                return newValue;
              })
            }
            options={[
              { id: "peg", label: "PEG" },
              { id: "ttf", label: "TTF" },
            ]}
            row
            className="items-center"
          />

          <Flexbox fullHeight className="gap-4 overflow-y-auto pb-1">
            <GraphFilter
              title="Légende"
              data={graphVisibility}
              handleOnChange={handleOnChangeGraphVisiblity}
            />

            {daily && !!daily.pegData.length && !!daily.ttfData.length && (
              <Flexbox fullWidth className="rounded-lg shadow-displayBoxShadow">
                <Flexbox
                  fullWidth
                  className="border-b-[1px] border-primary-300 bg-primary-200 p-1 text-sm font-medium text-black"
                >
                  <p className="w-full text-center text-sm">
                    Ecarts produits CAL actuels
                  </p>
                  <p className="w-full text-center text-sm text-primary">
                    {format(selectedDate, "dd/MM/yy")}
                  </p>
                </Flexbox>

                <Flexbox fullWidth>
                  <Flexbox row fullWidth className="daily-prods-grid">
                    <Flexbox
                      fullWidth
                      fullHeight
                      align="center"
                      justify="center"
                      className="bg-primary-200 p-1 text-sm font-medium text-black"
                    >
                      <p>PEG</p>
                    </Flexbox>

                    <Flexbox className="m-auto px-1 py-1">
                      {daily.pegData.map((item) => (
                        <Flexbox
                          key={item.title}
                          row
                          fullWidth
                          align="center"
                          justify="between"
                          className="mb-1 gap-3 text-xs"
                        >
                          <p>{item.title}</p>
                          <p>{item.value} €/MWh</p>
                        </Flexbox>
                      ))}
                    </Flexbox>
                  </Flexbox>

                  <Flexbox row fullWidth className="daily-prods-grid">
                    <Flexbox
                      fullWidth
                      fullHeight
                      align="center"
                      justify="center"
                      className="bg-primary-200 p-1 text-sm font-medium text-black"
                    >
                      <p>TTF</p>
                    </Flexbox>

                    <Flexbox className="m-auto px-1 py-1">
                      {daily.ttfData.map((item) => (
                        <Flexbox
                          key={item.title}
                          row
                          fullWidth
                          align="center"
                          justify="between"
                          className="mb-1 gap-3 text-xs"
                        >
                          <p>{item.title}</p>
                          <p>{item.value} €/MWh</p>
                        </Flexbox>
                      ))}
                    </Flexbox>
                  </Flexbox>
                </Flexbox>
              </Flexbox>
            )}

            <Flexbox fullWidth className="p-1">
              <TableGraphWidget<MarketYearlyDifferenceTableType>
                columns={columns}
                data={tableData ? tableData[tablePeriod] : []}
                tableBounds={tableBounds}
                hasLegend={false}
                theme="green"
                selectedPeriod={tablePeriod}
                setSelectedPeriod={setTablePeriod}
              />
            </Flexbox>
          </Flexbox>
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default GasCalendarsMarketDifference;
